import { apiFetch, isApiMode } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE REPORTING CENTRE, AS /sa SEES IT (#1043) — `GET /platform/reports`.
//
// The platform's figures — organizations, bookings, activities, sign-ins and
// card money — per month or per organization, with a platform total. The
// server validates the period, decides who may read (`platform.reports.read`,
// Entra only), aggregates in SQL and records the read (`reports.read`); this
// sends the period and hands back the answer. It never throws — every outcome
// is a state the section renders — and it carries refusal CODES, never
// sentences.
//
// AGGREGATES ONLY: the answer has no person in it. The one name it carries is
// an organization's, on the per-organization view.
// ─────────────────────────────────────────────────────────────────────

export type ReportGrouping = 'month' | 'organization';

/** A period: two local days, inclusive, `YYYY-MM-DD`. */
export interface ReportPeriod {
  from: string;
  to: string;
  groupBy: ReportGrouping;
}

export interface KindCounts {
  CENTRE: number;
  CLUB: number;
}

/** Money is integer øre, exactly as the ledger holds it. */
export interface ReportPayments {
  captures: number;
  grossOre: number;
  refunds: number;
  refundedOre: number;
  platformFeesOre: number;
}

export interface ReportFigures {
  bookings: number;
  courtMinutes: number;
  activities: number;
  signups: number;
  payments: ReportPayments;
}

export interface PlatformFigures extends ReportFigures {
  organizations: KindCounts;
  activeOrganizations: KindCounts;
  signedInAccounts: number;
  newAccounts: number;
}

export interface MonthRow extends PlatformFigures {
  /** `YYYY-MM`. */
  month: string;
  /** The part of the month the period covers. */
  from: string;
  to: string;
}

export interface OrganizationRow extends ReportFigures {
  organizationId: string;
  organizationName: string;
  kind: string;
  active: boolean;
}

export interface PlatformReport {
  period: ReportPeriod & { timeZone: string };
  months?: MonthRow[];
  organizations?: OrganizationRow[];
  total: PlatformFigures;
  /** Ledger entries in another currency than NOK — counted, never summed into kroner. */
  otherCurrencyEntries: number;
}

export type ReportResult =
  | { state: 'no-api' }
  | { state: 'ok'; report: PlatformReport }
  | { state: 'refused'; status: number; code: string | null; field: string | null }
  | { state: 'error'; status: number | null };

/** The query string for a period. All three are always sent. */
export function reportQuery(period: ReportPeriod): string {
  const params = new URLSearchParams();
  params.set('from', period.from);
  params.set('to', period.to);
  params.set('groupBy', period.groupBy);
  return `?${params.toString()}`;
}

/** The figures for a period. Every call is recorded by the server as a read. */
export async function fetchPlatformReport(period: ReportPeriod): Promise<ReportResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch(`/platform/reports${reportQuery(period)}`);
    if (res.ok) return { state: 'ok', report: (await res.json()) as PlatformReport };
    if (res.status >= 500) return { state: 'error', status: res.status };
    const body = (await res.json().catch(() => null)) as {
      error?: unknown;
      field?: unknown;
    } | null;
    return {
      state: 'refused',
      status: res.status,
      code: typeof body?.error === 'string' ? body.error : null,
      field: typeof body?.field === 'string' ? body.field : null,
    };
  } catch {
    return { state: 'error', status: null };
  }
}
